import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import { gsap } from 'gsap';

import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { Meta, productLd, breadcrumbLd } from '../seo/Meta.jsx';
import { MagneticLink } from '../anim/MagneticLink.jsx';
import { useLenis } from '../anim/useLenis.js';

/**
 * Pricing  -  institution-first.
 *
 * The $999 / institution Business tier is the primary product. Indie
 * ($29) and Team ($199) cascade down so individual evaluators and
 * smaller teams have low-friction entry points. Anchored against
 * generic competitor categories so the ROI math is obvious in the
 * first 5 seconds — no named competitors per legal hygiene.
 *
 * Static plan list  -  we no longer fetch from /v1/plans here, because
 * the marketing page must render the same numbers regardless of API
 * availability. The signup flow still validates against the live API.
 */

// Pricing matches docs/pricing.md (single source of truth) + the
// /v1/pricing JSON endpoint served by api/internal/http/billing_handler.go.
// Three tiers, metered overage. Stripe Tax disabled per PRD-004 §14.
const PLANS = [
  {
    id: 'indie',
    name: 'Indie',
    price: 29,
    cadence: '/ mo',
    highlight: false,
    audience: 'solo founders + builders evaluating one watchlist',
    features: [
      '1 user · 1 API key',
      '1,000 record-deliveries / mo included',
      '$0.005 per record over the included quota',
      '1 watchlist · 1 webhook destination',
      'All live portals + sub-threshold included',
      'Cancel anytime from customer portal',
    ],
    cta: 'Start with Indie',
  },
  {
    id: 'team',
    name: 'Team',
    price: 199,
    cadence: '/ mo',
    highlight: true,
    audience: 'procurement teams + analytics functions',
    features: [
      'Up to 5 users · multiple API keys',
      '10,000 record-deliveries / mo included',
      '$0.003 per record over the included quota',
      '5 watchlists · 5 webhook destinations',
      'Slack + email integrations',
      'Bulk archive download',
      'Cancel anytime',
    ],
    cta: 'Start with Team',
  },
  {
    id: 'business',
    name: 'Business',
    price: 999,
    cadence: '/ mo',
    highlight: false,
    audience: 'agencies, ministries, large primes',
    features: [
      'Unlimited users + API keys',
      '100,000 record-deliveries / mo included',
      '$0.001 per record over the included quota',
      'Unlimited watchlists + webhook destinations',
      'All integrations + bulk archive + raw upstream XML/JSON',
      'HMAC-SHA256 signed webhooks · idempotency keys',
      'Priority support · 24h response SLA',
      'Cancel anytime',
    ],
    cta: 'Start with Business',
  },
];

const COMPARES = [
  { name: 'Build it in-house', detail: '1 FTE eng / many portals / 18 months', price: '$220k+ / yr' },
  { name: 'Category-leading aggregator', detail: 'nightly bulk · CSV · no sub-threshold', price: '$15,000 / yr' },
  { name: 'Single-jurisdiction vendor', detail: 'single jurisdiction · weekly', price: '$19,800 / yr' },
  { name: 'tender-api · Business', detail: 'real-time · multi-jurisdiction · unlimited seats', price: '$11,988 / yr', ours: true },
];

export default function Pricing() {
  useLenis();
  const root = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.pricing-hero .h1-line > span', {
        yPercent: 102,
        stagger: 0.10,
        duration: 0.95,
        ease: 'expo.out',
      });
      gsap.from('.pricing-hero .lede', {
        autoAlpha: 0,
        y: 12,
        duration: 0.7,
        ease: 'expo.out',
        delay: 0.35,
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <>
      <Meta
        title="Pricing — $999 / month per institution"
        description="Institution licensing at $999 / month — unlimited seats inside one legal entity, 100,000 record-deliveries included, every live portal + sub-threshold register, signed webhooks. Compares to $220k+ for in-house build, $15k for category-leading nightly-bulk aggregators."
        path="/pricing"
        jsonLd={[
          breadcrumbLd([
            { name: 'tender-api', path: '/' },
            { name: 'Pricing', path: '/pricing' },
          ]),
          productLd({
            name: 'tender-api — global procurement intelligence API',
            description:
              'Per-institution licensing for global procurement-notice intelligence. Government portals normalised; minute-level freshness.',
            offers: PLANS.map((p) => ({
              name: p.name,
              price: p.price,
              users: p.id === 'indie' ? 1 : undefined,
            })),
          }),
        ]}
      />
      <Header />
      <main ref={root} className="container" style={{ paddingTop: 64, paddingBottom: 96 }}>
        <section className="pricing-hero">
          <h1 className="hero-headline mono">
            <span className="h1-line"><span>One product.</span></span>
            <span className="h1-line"><span>Three plans.</span></span>
            <span className="h1-line"><span className="accent">$999 / institution.</span></span>
          </h1>
          <p className="lede" style={{ maxWidth: 640, marginTop: 28 }}>
            Per-legal-entity licensing  -  one key for the whole department, ministry, agency,
            or prime contractor. Business covers the 90% case; Indie is the trial-by-one
            entry; Team scales to a procurement squad.
          </p>
        </section>

        {/* Plan grid */}
        <div className="plan-grid">
          {PLANS.map((p, i) => (
            <PlanCard key={p.id} plan={p} delay={i * 0.08} />
          ))}
        </div>

        {/* Anchor comparison */}
        <section className="anchor-table-wrap">
          <p className="eyebrow mono"><span className="dot" /> price · vs alternatives</p>
          <h2 className="section-title">What the rest of the market charges.</h2>
          <div className="anchor-table mono">
            {COMPARES.map((row, i) => (
              <motion.div
                key={i}
                className={`anchor-line ${row.ours ? 'is-ours' : ''}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-15%' }}
                transition={{ duration: 0.5, ease: [0.7, 0, 0.15, 1], delay: i * 0.06 }}
              >
                <span className="anchor-name">{row.name}</span>
                <span className="anchor-detail">{row.detail}</span>
                <span className={`anchor-price ${row.ours ? '' : 'is-strike'}`}>{row.price}</span>
              </motion.div>
            ))}
          </div>
          <p className="anchor-note mono">
            Prices verified Q2 2026 against published list-prices for category-leading
            commercial aggregators and single-jurisdiction vendors; in-house figure
            estimates a 4-week build per portal at $11k/wk loaded engineer.
          </p>
        </section>

        <p className="pricing-foot mono">
          Prices in USD · billed monthly via Stripe · cancel anytime · pay-per-record overage
          at the included-pool rate · ESM-equivalent VAT applied where required
        </p>
      </main>
      <Footer />
    </>
  );
}

function PlanCard({ plan, delay }) {
  return (
    <motion.article
      className={`plan-card ${plan.highlight ? 'is-primary' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-10%' }}
      transition={{ duration: 0.7, ease: [0.7, 0, 0.15, 1], delay }}
    >
      {plan.highlight && (
        <div className="plan-flag mono">most institutions pick this</div>
      )}
      <div className="plan-name mono">{plan.id}</div>
      <div className="plan-display">{plan.name}</div>
      <div className="plan-price-row">
        <span className="plan-price">${plan.price.toLocaleString('en-US')}</span>
        <span className="plan-cadence">{plan.cadence}</span>
      </div>
      <p className="plan-audience">{plan.audience}</p>
      <ul className="plan-features">
        {plan.features.map((f) => (
          <li key={f}>
            <Check size={14} strokeWidth={2.5} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div style={{ flex: 1 }} />
      <MagneticLink
        href={plan.id === 'sovereign' ? 'mailto:support@tender-api.com' : '/signup'}
        variant={plan.highlight ? 'primary' : 'secondary'}
        className="plan-cta"
        trailing={<ArrowRight size={14} strokeWidth={2.5} />}
      >
        {plan.cta}
      </MagneticLink>
    </motion.article>
  );
}
